import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import {
  FlowEditor,
  flowToValues,
  validateFlow,
  type FlowEditorValues,
} from '~/components/FlowEditor/FlowEditor';
import {
  useTenantFlow,
  useUpdateFlow,
  useDeleteFlow,
  findFormsUsingFlow,
} from '~/lib/flows-queries';
import { useTenantContext } from '~/lib/tenantContext';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { formatPbError } from '~/lib/pb-error';
import styles from './_authed.automations.templates_.editor.module.css';

export const Route = createFileRoute('/_authed/automations/flows_/$flowId')({
  component: EditFlowPage,
});

function EditFlowPage() {
  const { flowId } = Route.useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { tenants } = useTenantContext();
  const { data: flow, isLoading } = useTenantFlow(flowId);
  const update = useUpdateFlow();
  const del = useDeleteFlow();

  const [values, setValues] = useState<FlowEditorValues | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Hidratar values en useEffect — evita el bug React 19 #130 de setState durante render.
  useEffect(() => {
    if (flow && values === null) {
      setValues(flowToValues(flow));
    }
  }, [flow, values]);

  async function handleSave() {
    if (!values) return;
    setError(null);
    const validationError = validateFlow(values);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      await update.mutateAsync({
        id: flowId,
        patch: {
          name: values.name,
          description: values.description,
          trigger_type: values.trigger_type,
          trigger_config: values.trigger_config,
          is_active: values.is_active,
        },
      });
    } catch (err) {
      setError(formatPbError(err, 'Error al guardar'));
    }
  }

  async function handleDelete() {
    if (!flow) return;
    let usedBy: { id: string; name: string }[] = [];
    try { usedBy = await findFormsUsingFlow(flowId); } catch { /* ignore */ }
    const ok = await confirm({
      title: `Eliminar flujo "${flow.name}"`,
      description: (
        <>
          {usedBy.length > 0 && (
            <p style={{ marginTop: 0, fontWeight: 600 }}>
              ⚠️ Lo usa{usedBy.length > 1 ? 'n' : ''} {usedBy.length}{' '}
              formulario{usedBy.length > 1 ? 's' : ''} como flujo por defecto:{' '}
              {usedBy.map((f) => `«${f.name}»`).join(', ')}. Quedarán sin
              automatización al enviarse.
            </p>
          )}
          Se eliminan también todos los pasos del flujo. Las ejecuciones
          históricas se conservan para auditoría. Acción irreversible.
        </>
      ),
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await del.mutateAsync(flowId);
      void navigate({ to: '/automations/flows' });
    } catch (err) {
      setError(formatPbError(err, 'Error al eliminar'));
    }
  }

  if (isLoading || !values || !flow) {
    return (
      <div className={styles.page}>
        <p className="muted">Cargando flujo…</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/automations/flows" className={styles.backLink}>
          <ArrowLeft size={14} aria-hidden /> Flujos
        </Link>
        <div className={styles.titleRow}>
          <h2 className="h4" style={{ margin: 0 }}>{flow.name || 'Editar flujo'}</h2>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={handleDelete}
              disabled={del.isPending}
              title="Eliminar"
            >
              <Trash2 size={14} aria-hidden />
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleSave}
              disabled={update.isPending}
            >
              <Save size={14} aria-hidden />
              {update.isPending ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
        {error && <p className={styles.errorBanner}>{error}</p>}
      </header>

      <FlowEditor
        mode="edit"
        values={values}
        onChange={setValues}
        tenants={tenants}
        flowId={flowId}
        saving={update.isPending}
      />
    </div>
  );
}
